import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Space,
  Typography,
  Button,
  Alert,
} from 'antd';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import SidebarPage from '../../layout/SidebarPage/SidebarPage';
import PageTitle from '../../layout/PageTitle/PageTitle';
import StudentSelector from './components/StudentSelector';
import AttendanceLogTable from './components/AttendanceLogTable';
import AttendanceSummary from './components/AttendanceSummary';
import dayjs, { Dayjs } from 'dayjs';
import { useFetchUser } from '../../hooks/useFetchUser';
import { formatAppPin } from '../../utils/pin';

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface DateRange {
  start: string;
  end: string;
}

const AttendanceTrackerPage: React.FC = () => {
  const user = useFetchUser();
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );

  // Inicializar com o primeiro e último dia do mês atual
  const getDefaultDateRange = (): DateRange => {
    const now = dayjs();
    const startOfMonth = now.startOf('month');
    const endOfMonth = now.endOf('month');
    return {
      start: startOfMonth.format('YYYY-MM-DD'),
      end: endOfMonth.format('YYYY-MM-DD'),
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const [datePickerValue, setDatePickerValue] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  const handleStudentChange = (studentId: number | null) => {
    setSelectedStudentId(studentId);
  };

  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      const newDateRange = {
        start: dates[0].format('YYYY-MM-DD'),
        end: dates[1].format('YYYY-MM-DD'),
      };
      setDateRange(newDateRange);
      setDatePickerValue([dates[0], dates[1]]);
    }
  };

  const resetFilters = () => {
    setSelectedStudentId(null);
    const defaultRange = getDefaultDateRange();
    setDateRange(defaultRange);
    setDatePickerValue([dayjs().startOf('month'), dayjs().endOf('month')]);
  };

  const getAttendancePin = () => {
    if (user.data?.PrimaryGuardian?.id) {
      return formatAppPin('P', user.data.PrimaryGuardian.id);
    }
  };

  return (
    <SidebarPage>
      <div style={{ padding: '0 16px' }}>
        <PageTitle title="Attendance Tracker" />

        {user?.data?.PrimaryGuardian?.id && (
          <Alert
            style={{ marginBottom: 24 }}
            type="info"
            showIcon
            message="Attendance Tracker PIN"
            description={
              <Typography.Text
                copyable={{ text: getAttendancePin() ?? '' }}
                style={{ fontSize: 16 }}
              >
                {getAttendancePin()}
              </Typography.Text>
            }
            closable
          />
        )}

        <Card
          style={{ marginBottom: 24 }}
          title={
            <Space>
              <ClockCircleOutlined />
              Student Attendance Log
            </Space>
          }
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <StudentSelector
                value={selectedStudentId}
                onChange={handleStudentChange}
                placeholder="Select a student"
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: '100%' }}
                placeholder={['Start Date', 'End Date']}
                onChange={handleDateRangeChange}
                value={datePickerValue}
                suffixIcon={<CalendarOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                {selectedStudentId && (
                  <Button
                    type="link"
                    onClick={resetFilters}
                    style={{ padding: 0 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {selectedStudentId && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} lg={8}>
                <AttendanceSummary
                  studentId={selectedStudentId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                />
              </Col>
              <Col xs={24} lg={16}>
                <AttendanceLogTable
                  studentId={selectedStudentId}
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                />
              </Col>
            </Row>
          </>
        )}

        {!selectedStudentId && (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <ClockCircleOutlined
                style={{
                  fontSize: '48px',
                  color: '#bfbfbf',
                  marginBottom: '16px',
                }}
              />
              <Title level={4} type="secondary">
                Select a Student
              </Title>
              <Typography.Text type="secondary">
                Choose a student from the dropdown above to view their
                attendance log and summary.
              </Typography.Text>
            </div>
          </Card>
        )}
      </div>
    </SidebarPage>
  );
};

export default AttendanceTrackerPage;
